import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { api, Plan, PublicConfig, User } from './api';
import { appearanceFor, brandText } from './appearance';

type AppState = {
  user: User | null;
  isAdmin: boolean;
  demo: boolean;
  config: PublicConfig | null;
  plans: Plan[];
  loading: boolean;
  error: string;
  refresh(): Promise<void>;
  toast(message: string, tone?: 'success' | 'error'): void;
};

const AppContext = createContext<AppState | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setAdmin] = useState(false);
  const [demo, setDemo] = useState(false);
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState<{ message: string; tone: string } | null>(null);
  const initialized = useRef(false);
  const noticeTimer = useRef<number | undefined>(undefined);

  async function refresh() {
    if (!initialized.current) { setLoading(true); setError(''); }
    try {
      const publicData = await api.publicBootstrap();
      const session = await api.session().catch((error) => {
        if (window.Telegram?.WebApp?.initData) throw error;
        return null;
      });
      setConfig(session?.config || publicData.config);
      setPlans(publicData.plans);
      setUser(session?.user || null);
      setAdmin(session?.is_admin || false);
      setDemo(session?.demo || false);
      initialized.current = true;
      setError('');
    } catch (err) {
      const message = err instanceof Error ? err.message : '資料載入失敗';
      if (initialized.current) toast(message, 'error');
      else setError(message);
    } finally {
      setLoading(false);
    }
  }

  function toast(message: string, tone: 'success' | 'error' = 'success') {
    setNotice({ message, tone });
    window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setNotice(null), 4000);
  }

  useEffect(() => {
    if (!user || user.photo_url || demo) return;
    let cancelled = false;
    const userId = user.id;
    api.avatar().then(({ photo_url }) => {
      if (!cancelled && photo_url) setUser((current) => current?.id === userId ? { ...current, photo_url } : current);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id, user?.photo_url, demo]);

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    tg?.ready();
    tg?.expand();
    tg?.setHeaderColor?.('#ffffff');
    tg?.setBackgroundColor?.('#ffffff');
    refresh();
  }, []);

  useEffect(() => {
    if (!config) return;
    const a=appearanceFor(config);
    document.title=brandText(a.site_title || '{brand}',a);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content',a.accent_color);
    let icon=document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if(a.favicon_url || a.logo_url) {
      if(!icon) {icon=document.createElement('link');icon.rel='icon';document.head.appendChild(icon);}
      icon.href=a.favicon_url || a.logo_url;
    } else icon?.remove();
  }, [config]);

  return (
    <AppContext.Provider value={{ user, isAdmin, demo, config, plans, loading, error, refresh, toast }}>
      {children}
      {notice && <div className={`toast ${notice.tone}`} role={notice.tone === 'error' ? 'alert' : 'status'}>{notice.message}</div>}
    </AppContext.Provider>
  );
}

export function useApp() {
  const value = useContext(AppContext);
  if (!value) throw new Error('AppProvider missing');
  return value;
}

export function AppearancePreviewProvider({ value,children }: {value:Record<string,string>;children:ReactNode}) {
  const current=useApp();
  if(!current.config) return null;
  return <AppContext.Provider value={{...current,user:null,isAdmin:false,demo:true,loading:false,config:{...current.config,brand_name:value.brand_name,brand_tagline:value.brand_tagline,accent_color:value.accent_color,appearance:value}}}>{children}</AppContext.Provider>;
}
